import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import { getUserFacingApiMessage } from "../../utils/getUserFacingApiMessage";

const AI_MODES = [
  { value: "before_exception", labelKey: "chatbotConfigPage.aiIntentModeBefore" },
  { value: "when_exception_empty", labelKey: "chatbotConfigPage.aiIntentModeWhenEmpty" },
];

function normalizeConfidence(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.min(0.99, Math.max(0.5, Math.round(n * 100) / 100));
}

function parseConfigPayload(res) {
  const data = res?.data ?? res ?? {};
  const stored = data.stored ?? {};
  const effective = data.effective ?? {};
  return {
    platformAllowed: data.platformAllowed !== false,
    stored,
    effective,
  };
}

function draftFromConfig({ stored, effective }) {
  return {
    enabled: effective.enabled !== false,
    mode:
      stored.mode === "before_exception" || stored.mode === "when_exception_empty"
        ? stored.mode
        : effective.mode === "when_exception_empty"
          ? "when_exception_empty"
          : "before_exception",
    confidenceMin: normalizeConfidence(
      stored.confidenceMin != null ? stored.confidenceMin : effective.confidenceMin ?? 0.78
    ),
  };
}

export default function ChatbotSettingsDialog({
  userId,
  disabled,
  canEdit = true,
  onSuccess,
  onError,
}) {
  const { t } = useTranslation("settings");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [platformAllowed, setPlatformAllowed] = useState(true);
  const [draft, setDraft] = useState({ enabled: true, mode: "before_exception", confidenceMin: 0.78 });
  const [initialDraft, setInitialDraft] = useState(null);

  const loadConfig = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetchData(`/chatbot/ai-intent-config?user_id=${userId}`, "GET");
      const parsed = parseConfigPayload(res);
      setPlatformAllowed(parsed.platformAllowed);
      const nextDraft = draftFromConfig(parsed);
      setDraft(nextDraft);
      setInitialDraft(nextDraft);
    } catch (err) {
      const { message } = getUserFacingApiMessage(err);
      setLoadError(message || t("chatbotConfigPage.aiIntentLoadError"));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    if (open) loadConfig();
  }, [open, loadConfig]);

  const dirty = useMemo(() => {
    if (!initialDraft) return false;
    return (
      draft.enabled !== initialDraft.enabled ||
      draft.mode !== initialDraft.mode ||
      draft.confidenceMin !== initialDraft.confidenceMin
    );
  }, [draft, initialDraft]);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
    setLoadError(null);
    setInitialDraft(null);
  };

  const handleSave = async () => {
    if (!userId || !canEdit || !dirty) return;
    setSaving(true);
    setLoadError(null);
    try {
      const res = await fetchData("/chatbot/ai-intent-config", "PUT", {
        user_id: userId,
        enabled: draft.enabled,
        mode: draft.mode,
        confidenceMin: draft.confidenceMin,
      });
      const parsed = parseConfigPayload(res);
      const nextDraft = draftFromConfig(parsed);
      setDraft(nextDraft);
      setInitialDraft(nextDraft);
      setPlatformAllowed(parsed.platformAllowed);
      onSuccess?.(t("chatbotConfigPage.aiIntentSaveSuccess"));
      setOpen(false);
    } catch (err) {
      const { message } = getUserFacingApiMessage(err);
      const msg = message || t("chatbotConfigPage.aiIntentSaveError");
      setLoadError(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  const formDisabled = !canEdit || !platformAllowed || loading || saving;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<SettingsOutlinedIcon />}
        disabled={disabled || !userId}
        onClick={handleOpen}
        sx={{ textTransform: "none" }}
      >
        {t("chatbotConfigPage.settingsButton")}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("chatbotConfigPage.settingsTitle")}</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t("chatbotConfigPage.aiIntentLoading")}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              {loadError && (
                <Alert severity="error" onClose={() => setLoadError(null)}>
                  {loadError}
                </Alert>
              )}

              {!platformAllowed && (
                <Alert severity="warning">{t("chatbotConfigPage.aiIntentPlatformOff")}</Alert>
              )}

              {!canEdit && (
                <Alert severity="info">{t("chatbotConfigPage.settingsReadOnly")}</Alert>
              )}

              <Box>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t("chatbotConfigPage.aiIntentTitle")}
                  </Typography>
                  <Tooltip title={t("chatbotConfigPage.aiIntentTooltip")} arrow>
                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: "help" }} />
                  </Tooltip>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("chatbotConfigPage.aiIntentDescription")}
                </Typography>

                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!draft.enabled}
                        onChange={(e) => setDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
                        disabled={formDisabled}
                      />
                    }
                    label={t("chatbotConfigPage.aiIntentEnabled")}
                  />

                  <FormControl fullWidth size="small" disabled={formDisabled || !draft.enabled}>
                    <InputLabel id="chatbot-ai-intent-mode-label">
                      {t("chatbotConfigPage.aiIntentMode")}
                    </InputLabel>
                    <Select
                      labelId="chatbot-ai-intent-mode-label"
                      label={t("chatbotConfigPage.aiIntentMode")}
                      value={draft.mode}
                      onChange={(e) => setDraft((prev) => ({ ...prev, mode: e.target.value }))}
                    >
                      {AI_MODES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {draft.mode === "before_exception"
                        ? t("chatbotConfigPage.aiIntentModeBefore")
                        : t("chatbotConfigPage.aiIntentModeWhenEmpty")}
                    </FormHelperText>
                  </FormControl>

                  <TextField
                    label={t("chatbotConfigPage.aiIntentConfidence")}
                    type="number"
                    size="small"
                    fullWidth
                    disabled={formDisabled || !draft.enabled}
                    value={draft.confidenceMin ?? ""}
                    inputProps={{ min: 0.5, max: 0.99, step: 0.01 }}
                    helperText={t("chatbotConfigPage.aiIntentConfidenceHelper")}
                    onChange={(e) => {
                      const next = normalizeConfidence(e.target.value);
                      setDraft((prev) => ({ ...prev, confidenceMin: next }));
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving} sx={{ textTransform: "none" }}>
            {t("shared.cancel")}
          </Button>
          {canEdit && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || saving || !dirty || !platformAllowed}
              sx={{ textTransform: "none" }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : t("shared.save")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
